import { createCubejsApi } from '../utils/cube';

/**
 * Retrieves pixel anomaly detection data from Cube.js for a specified date range.
 *
 * This function is called from the Event Status view and fetches data related to pixel
 * anomalies using Cube.js. The data model supporting this query is defined in
 * `Cube/model/cubes`.
 *
 * @param {string} startDate - The start date of the query range (in YYYY-MM-DD format).
 * @param {string} endDate - The end date of the query range (in YYYY-MM-DD format).
 * @param {Object} cubejsApi - The Cube.js API instance used to execute the query.
 * @param {boolean} [limitToActive=false] - Optional parameter to filter for only active anomalies.
 *
 * @returns {Promise<Array>} A promise that resolves to an array of pixel anomaly data
 * formatted using `tablePivot`.
 */
export const getAnomalyDetectionData = (cubejsApi, limitToActive = false) => {
  const query = {
    dimensions: [
      'pixel_anomaly.id',
      'pixel_anomaly.action',
      'pixel_anomaly.anomaly_type',
      'pixel_anomaly.param',
      'pixel_anomaly.first_seen_exact',
      'pixel_anomaly.last_seen',
      'pixel_anomaly.pref_status',
      'pixel_anomaly.effective_status',
      'pixel_anomaly.failed_checks_count',
      'pixel_anomaly.total_checks_count',
      'pixel_anomaly.metric',
      'pixel_anomaly.metric_delta',
    ],
    order: {
      'pixel_anomaly.first_seen_exact': 'asc',
    },
  };

  if (limitToActive) {
    query.filters = [
      {
        member: 'pixel_anomaly.effective_status',
        operator: 'equals',
        values: ['active'],
      },
    ];
  }

  return cubejsApi.load(query).then(resultSet => resultSet.tablePivot());
};

/**
 * Retrieves dataset status information from Cube.js for a specified category and date range,
 * grouped by success and failure status.
 *
 * @param {string} category - The category to query ('spend', 'synthetic_events', or 'conversions')
 * @param {string} startDate - The start date of the query range (in YYYY-MM-DD format)
 * @param {string} endDate - The end date of the query range (in YYYY-MM-DD format)
 * @param {Object} cubejsApi - The Cube.js API instance used to execute the query
 * @param {number} [conversionSegmentId] - Optional parameter required only for 'conversions' category
 *
 * @returns {Promise<Object>} A promise that resolves to an object containing:
 *   - category: {string} The queried category
 *   - failures: {Array<Object>} Array of objects containing:
 *     - platform: {string} The platform name
 *     - type: {string} The type of data
 *     - dates: {Array<string>} Array of dates where failures occurred
 *   - successes: {Array<Object>} Array of objects containing:
 *     - platform: {string} The platform name
 *     - type: {string} The type of data
 *     - dates: {Array<string>} Array of dates where successes occurred
 */
export const getDatasetStatusRollupFromCube = (category, startDate, endDate, conversionSegmentId, jwt) => {
  const cubejsApi = createCubejsApi(jwt);
  const filters = [
    {
      member: 'data_status.category',
      operator: 'equals',
      values: [category],
    },
  ];

  if (category === 'conversions' && conversionSegmentId) {
    filters.push({
      member: 'data_status.conversion_segment_id',
      operator: 'equals',
      values: [conversionSegmentId.toString()],
    });
  }

  const query = {
    dimensions: [
      'data_status.platform',
      'data_status.type',
      'data_status.date',
      'data_status.status',
    ],
    filters,
    timeDimensions: [
      {
        dimension: 'data_status.date',
        dateRange: [startDate, endDate],
      },
    ],
  };

  return cubejsApi.load(query)
    .then(resultSet => {
      const grouped = resultSet.tablePivot().reduce((acc, row) => {
        const platform = row['data_status.platform'];
        const type = row['data_status.type'];
        const date = row['data_status.date'].replace('T', ' ').split('.')[0];
        const status = row['data_status.status'];

        if (status === 'success' || status === 'failure') {
          const key = `${platform}-${type}`;
          const targetArray = status === 'success' ? 'successes' : 'failures';

          return {
            ...acc,
            [targetArray]: {
              ...acc[targetArray],
              [key]: {
                platform,
                type,
                dates: acc[targetArray][key]
                  ? [...acc[targetArray][key].dates, date]
                  : [date],
              },
            },
          };
        }

        return acc;
      }, { successes: {}, failures: {} });

      return {
        category,
        failures: Object.values(grouped.failures),
        successes: Object.values(grouped.successes),
      };
    });
};

/**
 * Retrieves detailed dataset status information from Cube.js for a specified category and date range,
 * providing a daily status breakdown for each platform/type combination.
 *
 * @param {string} category - The category to query ('spend', 'synthetic_events', or 'conversions')
 * @param {string} startDate - The start date of the query range (in YYYY-MM-DD format)
 * @param {string} endDate - The end date of the query range (in YYYY-MM-DD format)
 * @param {Object} cubejsApi - The Cube.js API instance used to execute the query
 * @param {number} [conversionSegmentId] - Optional parameter required only for 'conversions' category
 *
 * @returns {Promise<Array<Object>>} A promise that resolves to an array of objects containing:
 *   - category: {string} The queried category
 *   - platform: {string} The platform name
 *   - type: {string} The type of data
 *   - statuses: {Array<Object>} Array of daily status records containing:
 *     - date: {string} The date in YYYY-MM-DD format
 *     - status: {string} The status for that date ('success', 'failure', etc.)
 */
export const getDatasetStatusFromCube = (category, startDate, endDate, conversionSegmentId, jwt) => {
  const cubejsApi = createCubejsApi(jwt);
  const filters = [
    {
      member: 'data_status.category',
      operator: 'equals',
      values: [category],
    },
  ];

  if (category === 'conversions' && conversionSegmentId) {
    filters.push({
      member: 'data_status.conversion_segment_id',
      operator: 'equals',
      values: [conversionSegmentId.toString()],
    });
  }

  const query = {
    dimensions: [
      'data_status.platform',
      'data_status.type',
      'data_status.date',
      'data_status.status',
    ],
    filters,
    timeDimensions: [
      {
        dimension: 'data_status.date',
        dateRange: [startDate, endDate],
      },
    ],
  };

  return cubejsApi.load(query)
    .then(resultSet => {
      const grouped = resultSet.tablePivot().reduce((acc, row) => {
        const platform = row['data_status.platform'];
        const type = row['data_status.type'];
        const date = row['data_status.date'].replace('T', ' ').split('.')[0];
        const status = row['data_status.status'];
        const key = `${platform}-${type}`;

        return {
          ...acc,
          [key]: {
            category,
            platform,
            type,
            statuses: [
              ...(acc[key]?.statuses || []),
              { date, status },
            ],
          },
        };
      }, {});

      return Object.values(grouped);
    });
};

/**
 * Retrieves spend data grouped by MMM (Marketing Mix Modeling) friendly names from Cube.js.
 *
 * This function fetches spend data for a specific conversion segment and date range,
 * aggregating the spend amounts by their MMM-friendly feature names. It includes tier_1
 * dimension due to transform table relation requirements.
 *
 * @param {string} jwt - JSON Web Token for authenticating with the Cube.js API
 * @param {number} conversionSegmentId - The ID of the conversion segment to filter by
 * @param {string} startDate - The start date of the query range (in YYYY-MM-DD format)
 * @param {string} endDate - The end date of the query range (in YYYY-MM-DD format)
 * @returns {Promise<Array<Object>>} A promise that resolves to an array of objects containing:
 *   - spend_mta_with_mmm_features.mmm_friendly_name: string
 *   - spend_mta_with_mmm_features.spend: number
 */
export const getSpendByMMMFriendlyName = (jwt, conversionSegmentId, startDate, endDate) => {
  const cubejsApi = createCubejsApi(jwt);
  const query = {
    dimensions: [
      'spend_mta_with_mmm_features.mmm_friendly_name',
    ],
    filters: [
      {
        member: 'spend_mta_with_mmm_features.conversion_segment_id',
        operator: 'equals',
        values: [conversionSegmentId.toString()],
      },
    ],
    measures: ['spend_mta_with_mmm_features.spend'],
    timeDimensions: [
      {
        dimension: 'spend_mta_with_mmm_features.date',
        dateRange: [startDate, endDate],
      },
    ],
  };

  return cubejsApi.load(query).then(resultSet => resultSet.tablePivot());
};

/**
 * Retrieves MMM feature classifications from Cube.js.
 *
 * This function fetches feature classifications for MMM (Marketing Mix Modeling) from Cube.js.
 *
 * @param {string} jwt - JSON Web Token for authenticating with the Cube.js API
 * @returns {Promise<Array<Object>>} A promise that resolves to an array of objects containing:
 *   - mmm_feature_aliases.feature_id: string
 *   - mmm_feature_aliases.friendly_name: string
 *   - mmm_feature_aliases.channel: string
 *   - mmm_feature_aliases.platform: string
 *   - mmm_feature_aliases.audience_targeting: string
 *   - mmm_feature_aliases.ad_type: string
 */
export const getMMMFeatureClassifications = jwt => {
  const cubejsApi = createCubejsApi(jwt);
  const query = {
    dimensions: [
      'mmm_feature_aliases.feature_id',
      'mmm_feature_aliases.friendly_name',
      'mmm_feature_aliases.channel',
      'mmm_feature_aliases.platform',
      'mmm_feature_aliases.audience_targeting',
      'mmm_feature_aliases.ad_type',
    ],
  };

  return cubejsApi.load(query)
    .then(resultSet => resultSet.tablePivot());
};
